import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import ScrollLink from '../common/ScrollLink';
import { theme } from '../../styles/theme';
import MobileFooter from './mobile/MobileFooter';
import { WebsitePrimaryButton } from '../common/websiteSection';
import { getNeatScheduleCopyrightYears } from '../../utils/copyrightYear';

const FooterContainer = styled.footer`
  background: #0f172a;
  color: #ffffff;
  padding: ${theme.spacing['3xl']} ${theme.spacing.xl} ${theme.spacing.xl};
`;

const FooterGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.4fr repeat(3, 1fr);
  gap: ${theme.spacing['2xl']};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const NewsletterBox = styled.div`
  h4 {
    margin: 0 0 ${theme.spacing.sm};
    font-size: ${theme.typography.fontSize.lg};
    font-weight: 700;
  }

  p {
    margin: 0 0 ${theme.spacing.md};
    color: rgba(255, 255, 255, 0.65);
    font-size: ${theme.typography.fontSize.sm};
    line-height: 1.5;
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};

  input {
    flex: 1;
    min-width: 180px;
    padding: 12px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
    font: inherit;
    font-size: ${theme.typography.fontSize.sm};

    &::placeholder {
      color: rgba(255, 255, 255, 0.45);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};
`;

const SocialIcon = styled.a`
  color: rgba(255, 255, 255, 0.65);
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover {
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.08);
  }

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }
`;

const FooterColumn = styled.div`
  h5 {
    margin: 0 0 ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.xs};
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.45);
  }
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FooterLink = styled(ScrollLink)`
  color: rgba(255, 255, 255, 0.72);
  text-decoration: none;
  font-size: ${theme.typography.fontSize.sm};
  transition: color 0.2s ease;

  &:hover {
    color: #ffffff;
  }
`;

const FooterPageLink = styled(Link)`
  color: rgba(255, 255, 255, 0.72);
  text-decoration: none;
  font-size: ${theme.typography.fontSize.sm};
  transition: color 0.2s ease;

  &:hover {
    color: #ffffff;
  }
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: ${theme.spacing['2xl']} auto 0;
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  text-align: center;
  gap: ${theme.spacing.sm};
  color: rgba(255, 255, 255, 0.45);
  font-size: ${theme.typography.fontSize.sm};

  a {
    color: rgba(255, 255, 255, 0.55);
    text-decoration: none;

    &:hover {
      color: #ffffff;
    }
  }
`;

const Footer = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleNewsletter = (e) => {
    e.preventDefault();
  };

  if (isMobile) {
    return <MobileFooter />;
  }

  return (
    <FooterContainer>
      <FooterGrid>
        <NewsletterBox>
          <h4>Subscribe for scheduling insights</h4>
          <p>Tips on bookings, staff management, and growing your service business.</p>
          <NewsletterForm onSubmit={handleNewsletter}>
            <input type="email" placeholder="Email address" aria-label="Email address" />
            <WebsitePrimaryButton type="submit" style={{ borderRadius: '999px', padding: '12px 20px' }}>
              Subscribe
            </WebsitePrimaryButton>
          </NewsletterForm>
          <SocialLinks>
            <SocialIcon href="https://www.facebook.com/neatschedule" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
              </svg>
            </SocialIcon>
            <SocialIcon href="https://x.com/neatschedule" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
              </svg>
            </SocialIcon>
          </SocialLinks>
        </NewsletterBox>

        <FooterColumn>
          <h5>Product</h5>
          <LinkList>
            <li><FooterLink to="/why">Why Us</FooterLink></li>
            <li><FooterLink to="/#features">Features</FooterLink></li>
            <li><FooterLink to="/#pricing">Pricing</FooterLink></li>
            <li><FooterLink to="/#comparison">Compare Plans</FooterLink></li>
          </LinkList>
        </FooterColumn>

        <FooterColumn>
          <h5>Company</h5>
          <LinkList>
            <li><FooterPageLink to="/about">About Us</FooterPageLink></li>
            <li><FooterPageLink to="/contact">Contact</FooterPageLink></li>
          </LinkList>
        </FooterColumn>

        <FooterColumn>
          <h5>Legal</h5>
          <LinkList>
            <li><FooterPageLink to="/main/privacy-policy">Privacy Policy</FooterPageLink></li>
            <li><FooterPageLink to="/main/cookie-policy">Cookie Policy</FooterPageLink></li>
            <li><FooterPageLink to="/main/terms">Terms of Service</FooterPageLink></li>
            <li><FooterPageLink to="/main/refund-policy">Refund Policy</FooterPageLink></li>
          </LinkList>
        </FooterColumn>
      </FooterGrid>

      <FooterBottom>
        <span>© {getNeatScheduleCopyrightYears()} Neat Schedule. A <a href="https://covingtech.com" target="_blank" rel="noopener noreferrer">CovingTech</a> Company.</span>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;
